import { Clock } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from 'react'
import { createPortal } from 'react-dom'
import { MARKET_HOURS } from '../../config/constants'

interface BaseTimePickerProps {
    value: string
    onChange: (value: string) => void
    minTime?: string
    maxTime?: string
}

export const BaseTimePicker: React.FC<BaseTimePickerProps> = ({ value, onChange, minTime, maxTime }) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const clockRef = useRef<HTMLDivElement>(null)
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

    const [view, setView] = useState<'hours' | 'minutes'>('hours')
    const [selectedHour, setSelectedHour] = useState(() => (value || MARKET_HOURS.BASE_TIME_STR).split(':')[0] || String(MARKET_HOURS.BASE_HOUR).padStart(2, '0'))
    const [selectedMinute, setSelectedMinute] = useState(() => (value || MARKET_HOURS.BASE_TIME_STR).split(':')[1] || String(MARKET_HOURS.BASE_MINUTE).padStart(2, '0'))
    const [isDragging, setIsDragging] = useState(false)

    const updatePosition = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            const dropdownWidth = 280
            const dropdownHeight = 410 // approximate height
            let left = rect.left
            let top = rect.bottom + 12

            // Handle horizontal overflow
            if (left + dropdownWidth > window.innerWidth) {
                left = window.innerWidth - dropdownWidth - 20
            }

            // Handle vertical overflow
            if (top + dropdownHeight > window.innerHeight) {
                if (rect.top - dropdownHeight - 12 > 0) {
                    // Open upwards if space exists
                    top = rect.top - dropdownHeight - 12
                } else {
                    // Force it inside the viewport
                    top = Math.max(20, window.innerHeight - dropdownHeight - 20)
                }
            }

            setDropdownStyle({
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                width: `${dropdownWidth}px`,
                zIndex: 99999,
            })
        }
    }, [])

    useEffect(() => {
        if (value) {
            const parts = value.split(':')
            setSelectedHour(parts[0] || String(MARKET_HOURS.BASE_HOUR).padStart(2, '0'))
            setSelectedMinute(parts[1] || String(MARKET_HOURS.BASE_MINUTE).padStart(2, '0'))
        }
    }, [value])

    useEffect(() => {
        if (isOpen) {
            setView('hours') // Reset to hours view on open
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                const target = event.target as Element
                if (!target.closest('.base-time-picker-dropdown')) {
                    setIsOpen(false)
                }
            }
        }

        const handleUpdate = () => {
            if (isOpen) updatePosition()
        }

        if (isOpen) {
            updatePosition()
            document.addEventListener('mousedown', handleClickOutside)
            window.addEventListener('scroll', handleUpdate, true)
            window.addEventListener('resize', handleUpdate)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('scroll', handleUpdate, true)
            window.removeEventListener('resize', handleUpdate)
        }
    }, [isOpen, updatePosition])

    const handleConfirm = () => {
        let finalH = selectedHour
        let finalM = selectedMinute

        const totalMinutes = parseInt(finalH, 10) * 60 + parseInt(finalM, 10)
        
        let minBoundH = MARKET_HOURS.OPEN_HOUR;
        let minBoundM = MARKET_HOURS.OPEN_MINUTE;
        if (minTime) {
            const parts = minTime.split(':');
            minBoundH = parseInt(parts[0] || `${MARKET_HOURS.OPEN_HOUR}`, 10);
            minBoundM = parseInt(parts[1] || `${MARKET_HOURS.OPEN_MINUTE}`, 10);
        }
        const minMarketMinutes = minBoundH * 60 + minBoundM;

        let maxBoundH = MARKET_HOURS.CLOSE_HOUR;
        let maxBoundM = MARKET_HOURS.CLOSE_MINUTE;
        if (maxTime) {
            const parts = maxTime.split(':');
            maxBoundH = parseInt(parts[0] || `${MARKET_HOURS.CLOSE_HOUR}`, 10);
            maxBoundM = parseInt(parts[1] || `${MARKET_HOURS.CLOSE_MINUTE}`, 10);
        }
        const maxMarketMinutes = maxBoundH * 60 + maxBoundM;

        if (totalMinutes < minMarketMinutes) {
            finalH = minBoundH.toString().padStart(2, '0')
            finalM = minBoundM.toString().padStart(2, '0')
        } else if (totalMinutes > maxMarketMinutes) {
            finalH = maxBoundH.toString().padStart(2, '0')
            finalM = maxBoundM.toString().padStart(2, '0')
        }

        startTransition(() => {
            onChange(`${finalH}:${finalM}`)
        })
        setIsOpen(false)
    }

    // --- Analog Clock Logic ---
    const handleInteraction = useCallback((clientX: number, clientY: number) => {
        if (!clockRef.current) return;
        const rect = clockRef.current.getBoundingClientRect();
        const CLOCK_RADIUS = 110;
        const x = clientX - rect.left - CLOCK_RADIUS;
        const y = clientY - rect.top - CLOCK_RADIUS;
        let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;

        let minH = minTime ? parseInt(minTime.split(':')[0] || `${MARKET_HOURS.OPEN_HOUR}`, 10) : MARKET_HOURS.OPEN_HOUR;
        let maxH = maxTime ? parseInt(maxTime.split(':')[0] || `${MARKET_HOURS.CLOSE_HOUR}`, 10) : MARKET_HOURS.CLOSE_HOUR;

        if (view === 'hours') {
            let hour = Math.round(angle / 30);
            if (hour === 0) hour = 12;
            const pmSpan = maxH > 12 ? maxH - 12 : 3;
            if (hour >= 1 && hour <= pmSpan) hour += 12;
            
            if (hour < minH) hour = minH;
            if (hour > maxH) hour = maxH;
            
            setSelectedHour(hour.toString().padStart(2, '0'));
        } else {
            let minute = Math.round(angle / 6);
            if (minute === 60) minute = 0;
            setSelectedMinute(minute.toString().padStart(2, '0'));
        }
    }, [view, minTime, maxTime]);

    useEffect(() => {
        if (isDragging) {
            const handleMouseMove = (e: MouseEvent) => {
                e.preventDefault();
                handleInteraction(e.clientX, e.clientY);
            };
            const handleMouseUp = () => {
                setIsDragging(false);
                if (view === 'hours') {
                    setTimeout(() => setView('minutes'), 300);
                }
            };
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleInteraction, view]);

    const CLOCK_SIZE = 220;
    const NUMBER_RADIUS = 85;
    const activeAngle = view === 'hours' 
        ? (parseInt(selectedHour) % 12) * 30
        : parseInt(selectedMinute) * 6;

    const renderNumbers = () => {
        let items: number[] = [];
        if (view === 'hours') {
            let minH = minTime ? parseInt(minTime.split(':')[0] || `${MARKET_HOURS.OPEN_HOUR}`, 10) : MARKET_HOURS.OPEN_HOUR;
            let maxH = maxTime ? parseInt(maxTime.split(':')[0] || `${MARKET_HOURS.CLOSE_HOUR}`, 10) : MARKET_HOURS.CLOSE_HOUR;
            for (let i = minH; i <= maxH; i++) {
                items.push(i);
            }
        } else {
            items = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        }

        return items.map((val) => {
            let angle = view === 'hours' ? (val % 12) * 30 : val * 6;
            const display = val.toString().padStart(2, '0');
            const rad = (angle - 90) * (Math.PI / 180);
            const x = Math.cos(rad) * NUMBER_RADIUS;
            const y = Math.sin(rad) * NUMBER_RADIUS;

            const isActive = view === 'hours' 
                ? parseInt(selectedHour) === val 
                : parseInt(selectedMinute) === val;

            return (
                <div
                    key={val}
                    className={`absolute top-1/2 left-1/2 w-8 h-8 flex items-center justify-center text-[13px] font-black rounded-full pointer-events-none transition-colors duration-300 z-20 ${isActive ? 'text-white' : 'text-slate-600 dark:text-slate-300'}`}
                    style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                >
                    {display}
                </div>
            );
        });
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center outline-none bg-transparent border-none p-0"
            >
                <span className="text-slate-700 dark:text-slate-100 text-[12px] md:text-[13px] font-black tracking-tight premium-cell-mono hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                    {value || MARKET_HOURS.BASE_TIME_STR}
                </span>
            </button>

            {isOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="base-time-picker-dropdown bg-white/95 dark:bg-slate-900/95 backdrop-blur-3xl rounded-[24px] shadow-[0_20px_60px_-15px_rgba(79,70,229,0.3)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/5 dark:ring-white/5 overflow-hidden animate-zoom-in duration-300 select-none"
                        style={dropdownStyle}
                    >
                        {/* Premium Header */}
                        <div className="relative p-5 pb-2">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 blur-[40px] rounded-full pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-violet-500/20 blur-[40px] rounded-full pointer-events-none" />
                            
                            <div className="relative z-10 flex flex-col items-center justify-center">
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 mb-3">
                                    Select Base Time
                                </p>
                                <div className="flex items-center justify-center gap-3">
                                    <button 
                                        onClick={() => setView('hours')}
                                        className={`w-16 h-16 flex items-center justify-center rounded-[18px] transition-all duration-300 border-2 ${view === 'hours' ? 'bg-indigo-50/50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500' : 'bg-transparent text-slate-500 dark:text-slate-400 border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                    >
                                        <div className="text-3xl font-black premium-cell-mono tracking-tighter">{selectedHour}</div>
                                    </button>
                                    <div className="flex flex-col gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                                    </div>
                                    <button 
                                        onClick={() => setView('minutes')}
                                        className={`w-16 h-16 flex items-center justify-center rounded-[18px] transition-all duration-300 border-2 ${view === 'minutes' ? 'bg-indigo-50/50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500' : 'bg-transparent text-slate-500 dark:text-slate-400 border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                    >
                                        <div className="text-3xl font-black premium-cell-mono tracking-tighter">{selectedMinute}</div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Analog Clock Face */}
                        <div className="py-4 flex justify-center items-center relative">
                            <div 
                                ref={clockRef}
                                className="relative rounded-full bg-slate-100/60 dark:bg-slate-800/60 shadow-inner border border-slate-200/50 dark:border-slate-700/50 cursor-pointer touch-none z-0"
                                style={{ width: CLOCK_SIZE, height: CLOCK_SIZE }}
                                onMouseDown={(e) => {
                                    setIsDragging(true);
                                    handleInteraction(e.clientX, e.clientY);
                                }}
                            >
                                {/* Center Dot */}
                                <div className="absolute top-1/2 left-1/2 w-2 h-2 -ml-1 -mt-1 bg-indigo-500 rounded-full z-20" />
                                
                                {/* Clock Hand */}
                                <div 
                                    className={`absolute bottom-1/2 left-1/2 w-[2px] bg-indigo-500 pointer-events-none z-10 ${isDragging ? '' : 'transition-transform duration-300 ease-out'}`}
                                    style={{ 
                                        height: NUMBER_RADIUS, 
                                        transformOrigin: 'bottom center',
                                        transform: `translateX(-50%) rotate(${activeAngle}deg)`
                                    }}
                                >
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34px] h-[34px] bg-indigo-500 rounded-full flex items-center justify-center shadow-md shadow-indigo-500/40">
                                        {/* Show small inner dot if exact minute picked isn't a multiple of 5 */}
                                        {view === 'minutes' && parseInt(selectedMinute) % 5 !== 0 && (
                                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                        )}
                                    </div>
                                </div>

                                {/* Numbers */}
                                {renderNumbers()}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="p-5 pt-3 flex gap-3 relative z-10">
                            <button
                                onClick={() => setIsOpen(false)}
                                className="flex-1 py-3 rounded-[16px] text-[13px] font-black text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all active:scale-95"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="flex-1 py-3 rounded-[16px] text-[13px] font-black text-white bg-indigo-500 hover:bg-indigo-600 shadow-md shadow-indigo-500/30 transition-all active:scale-95"
                            >
                                CONFIRM
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
